import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, Lock, Printer } from 'lucide-react';
import { SYSTEM_DOCUMENTS } from '@platform/shared-types';
import type { DocumentKind, LegalDocument, SystemDocumentDef } from '@platform/shared-types';
import { RubyGemMark } from '../../components/RubyGemMark';
import { useStaffAccess } from '../../hooks/useStaffAccess';
import { toastService } from '../../services/toastService';
import { documentsApi, publicDocumentUrl } from '../../services/documentsApi';
import type { VersionSummary } from '../../services/documentsApi';
import {
  activeVersionRow, documentHeadState, formatUtcDay, headVersion, latestPublishedVersion, systemDocPhase,
} from './legalFormat';
import type { SystemDocPhase } from './legalFormat';
import { Tag } from './rubyChrome';
import ShareDocumentModal, { copyText } from './ShareDocumentModal';

/**
 * SYSTEM DOCUMENTS — the built-in master legal set (owner 2026-10-09), shown
 * ABOVE the existing library on /legal/policies and /legal/agreements.
 * Approved mockup: platform-app/mockups/system-documents-and-legal-center.html,
 * screen A.
 *
 * PURELY ADDITIVE. The library below keeps working exactly as before; these
 * rows also appear there in their "System documents" folder. A system document
 * cannot be retired — there is no Retire/Delete here, only new versions.
 *
 * Data: the documents the page already fetched. `GET /admin/documents` now
 * projects `versions` / `activeVersionNo` per row, so no extra request.
 */

type Listed = LegalDocument & {
  versions?: VersionSummary[];
  activeVersionNo?: number | null;
};

type Line = 'rubytill' | 'rubyestate' | 'company';
const LINES: { id: Line; label: string }[] = [
  { id: 'rubytill', label: 'Ruby Till' },
  { id: 'rubyestate', label: 'Ruby Estate' },
  // Governance records (bylaws, operating agreement…) — private by default.
  { id: 'company', label: 'Company' },
];

const INK = '#0A0A0D';

const CHIP: Record<SystemDocPhase, string> = {
  empty: 'bg-amber-100 text-amber-800',
  live: 'bg-emerald-100 text-emerald-800',
  review: 'bg-sky-100 text-sky-800',
  draft: 'bg-stone-100 text-stone-600',
};

export default function SystemDocumentsPanel({
  kind, documents, loading, detailBase,
}: {
  kind: DocumentKind;
  /** The page's own list (already filtered to `kind`). */
  documents: readonly LegalDocument[];
  loading: boolean;
  /** e.g. `/legal/policies` — rows link to `${detailBase}/:documentId`. */
  detailBase: string;
}) {
  const navigate = useNavigate();
  const access = useStaffAccess();
  const canEdit = access.levelForPath(detailBase) === 'edit';

  const [line, setLine] = useState<Line>('rubytill');
  /** documentId → visibility set here and not yet reflected in `documents`. */
  const [visOverride, setVisOverride] = useState<Record<string, 'public' | 'private'>>({});
  const [savingVis, setSavingVis] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [printing, setPrinting] = useState<string | null>(null);
  const [sharing, setSharing] = useState<{ doc: Listed; visibility: 'public' | 'private' } | null>(null);

  // A fresh list from the server is the truth again.
  useEffect(() => { setVisOverride({}); }, [documents]);

  /** Registry order, matched to the live rows by slug (the permanent key). */
  const rows = useMemo(() => {
    const bySlug = new Map<string, Listed>();
    (documents as Listed[]).forEach((d) => { if (d.system) bySlug.set(d.slug, d); });
    const out: { def: SystemDocumentDef; doc: Listed }[] = [];
    SYSTEM_DOCUMENTS.forEach((def) => {
      const doc = bySlug.get(def.slug);
      // The STORED kind decides the page: an adopted library row keeps its own.
      if (doc && doc.kind === kind) out.push({ def, doc });
    });
    return out;
  }, [documents, kind]);

  const counts = useMemo(() => {
    const c: Record<Line, number> = { rubytill: 0, rubyestate: 0, company: 0 };
    rows.forEach((r) => { c[r.def.productLine as Line] += 1; });
    return c;
  }, [rows]);

  const shown = useMemo(() => rows.filter((r) => r.def.productLine === line), [rows, line]);

  const stats = useMemo(() => {
    const s: Record<SystemDocPhase, number> = { empty: 0, live: 0, review: 0, draft: 0 };
    shown.forEach(({ doc }) => { s[systemDocPhase(doc.versions ?? [], doc.activeVersionNo)] += 1; });
    return s;
  }, [shown]);

  if (!loading && rows.length === 0) return null;

  const visibilityOf = (doc: Listed): 'public' | 'private' =>
    visOverride[doc.documentId] ?? (doc.visibility === 'private' ? 'private' : 'public');

  const setVisibility = async (doc: Listed, next: 'public' | 'private') => {
    const prev = visibilityOf(doc);
    if (prev === next || savingVis) return;
    setVisOverride((m) => ({ ...m, [doc.documentId]: next }));
    setSavingVis(doc.documentId);
    try {
      await documentsApi.setVisibility(doc.documentId, next);
      toastService.success(`${doc.title} is now ${next}`);
    } catch (e: any) {
      setVisOverride((m) => ({ ...m, [doc.documentId]: prev }));
      toastService.error(`Could not change visibility: ${e?.message || 'update_failed'}`);
    } finally {
      setSavingVis(null);
    }
  };

  const copyLink = async (doc: Listed) => {
    if (await copyText(publicDocumentUrl(doc.slug))) {
      setCopied(doc.documentId);
      window.setTimeout(() => setCopied((c) => (c === doc.documentId ? null : c)), 1600);
    } else {
      toastService.error('Could not copy — open Share to select the link.');
    }
  };

  /**
   * The published version's frozen PDF (HTML if no PDF), in a new tab. With
   * nothing published, the public page — it says the document is being prepared.
   * The tab is opened synchronously so the popup blocker allows it.
   */
  const print = async (doc: Listed) => {
    const versions = doc.versions ?? [];
    const target = doc.activeVersionNo ?? latestPublishedVersion(versions)?.versionNo ?? null;
    const fallback = publicDocumentUrl(doc.slug);
    const w = window.open('', '_blank');
    if (target === null) {
      if (w) w.location.href = fallback; else window.open(fallback, '_blank', 'noopener');
      return;
    }
    setPrinting(doc.documentId);
    try {
      const r = await documentsApi.getVersion(doc.documentId, target);
      const arts = ((r.version as any)?.artifacts ?? []) as { format: string; url?: string }[];
      const url = arts.find((a) => a.format === 'pdf' && a.url)?.url
        ?? arts.find((a) => a.format === 'html' && a.url)?.url
        ?? fallback;
      if (w) w.location.href = url; else window.open(url, '_blank', 'noopener');
    } catch (e: any) {
      if (w) w.close();
      toastService.error(`Could not open the published file: ${e?.message || 'unavailable'}`);
    } finally {
      setPrinting(null);
    }
  };

  const chip = (doc: Listed) => {
    const versions = doc.versions ?? [];
    const phase = systemDocPhase(versions, doc.activeVersionNo);
    if (phase === 'empty') return <span className={`rounded-full px-2.5 py-1 text-[12px] font-semibold ${CHIP.empty}`}>Empty draft</span>;
    if (phase === 'live') {
      const active = activeVersionRow(versions, doc.activeVersionNo ?? null);
      const head = headVersion(versions);
      const pending = head && head.versionNo !== doc.activeVersionNo && head.status !== 'published' ? head : null;
      return (
        <span className="flex flex-wrap gap-1">
          <span className={`whitespace-nowrap rounded-full px-2.5 py-1 text-[12px] font-semibold ${CHIP.live}`}>
            v{doc.activeVersionNo} live{active?.effectiveAt ? ` · ${formatUtcDay(active.effectiveAt)}` : ''}
          </span>
          {pending && (
            <span className="whitespace-nowrap rounded-full bg-stone-100 px-2 py-1 text-[11px] text-stone-600">
              v{pending.versionNo} {pending.status === 'draft' ? 'draft' : pending.status === 'approved' ? 'approved' : 'in review'}
            </span>
          )}
        </span>
      );
    }
    if (phase === 'review') {
      const head = headVersion(versions);
      return (
        <span className={`whitespace-nowrap rounded-full px-2.5 py-1 text-[12px] font-semibold ${CHIP.review}`}>
          v{head?.versionNo} {head?.status === 'approved' ? 'approved' : 'in review'}
        </span>
      );
    }
    const state = documentHeadState(doc.status, versions);
    return <Tag tone={state.tone}>{state.label}</Tag>;
  };

  const btn = 'inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg border border-stone-200 bg-white px-3 text-[13px] font-semibold text-stone-700 hover:border-stone-400 disabled:opacity-50';

  return (
    <section className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stone-200 px-4 py-3.5 sm:px-5">
        <h2 className="flex flex-wrap items-center gap-2.5 text-[16px] font-semibold text-stone-900">
          <Lock className="h-4 w-4 text-stone-500" />
          System documents
          <span className="rounded-full px-2 py-1 text-[11px] font-semibold text-white" style={{ background: INK }}>
            Built-in · cannot be removed
          </span>
        </h2>
        <div className="flex gap-1.5" role="tablist" aria-label="Product line">
          {LINES.map((l) => (
            <button
              key={l.id}
              type="button"
              role="tab"
              aria-selected={line === l.id}
              onClick={() => setLine(l.id)}
              className={`min-h-[44px] rounded-full border px-4 text-[13px] font-semibold transition-colors ${
                line === l.id
                  ? 'border-[#DC1A3C] bg-[#DC1A3C] text-white'
                  : 'border-stone-200 bg-white text-stone-700 hover:border-stone-400'
              }`}
            >
              {l.label} · {counts[l.id]}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-x-5 gap-y-1 border-b border-stone-200 px-4 py-2.5 text-[13px] text-stone-500 sm:px-5">
        <span><b className="text-stone-900">{stats.live}</b> live</span>
        <span><b className="text-stone-900">{stats.review}</b> in review</span>
        {stats.draft > 0 && <span><b className="text-stone-900">{stats.draft}</b> drafting</span>}
        <span><b className="text-stone-900">{stats.empty}</b> empty — need writing before launch</span>
      </div>

      {loading && rows.length === 0 ? (
        <div className="flex justify-center py-8"><RubyGemMark size={36} drawing /></div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[14px]">
            <thead>
              <tr className="bg-stone-50">
                {[
                  { h: 'Document', cls: '' },
                  { h: 'Status', cls: '' },
                  { h: 'Fixed link', cls: 'hidden sm:table-cell' },
                  { h: 'Who can open', cls: '' },
                  { h: '', cls: '' },
                ].map(({ h, cls }) => (
                  <th
                    key={h || 'actions'}
                    className={`whitespace-nowrap px-3 py-2.5 text-left text-[11px] font-bold uppercase tracking-wider text-stone-500 sm:px-4 ${cls}`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map(({ def, doc }) => {
                const vis = visibilityOf(doc);
                const phase = systemDocPhase(doc.versions ?? [], doc.activeVersionNo);
                const link = publicDocumentUrl(doc.slug).replace(/^https?:\/\//, '');
                return (
                  <tr key={doc.documentId} className="border-t border-stone-100 align-middle">
                    <td className="px-3 py-3 sm:px-4">
                      <div className="font-semibold text-stone-900">{doc.title}</div>
                      <div className="mt-0.5 text-[12px] text-stone-500">{def.purpose}</div>
                    </td>
                    <td className="px-3 py-3 sm:px-4">{chip(doc)}</td>
                    <td className="hidden px-3 py-3 sm:table-cell sm:px-4">
                      <div className="flex items-center gap-1.5 font-mono text-[12px] text-stone-600">
                        <span className="max-w-[260px] truncate" title={publicDocumentUrl(doc.slug)}>{link}</span>
                        <button
                          type="button"
                          title="Copy link"
                          aria-label={`Copy link to ${doc.title}`}
                          onClick={() => void copyLink(doc)}
                          className="inline-grid h-11 min-w-[44px] place-items-center rounded-lg border border-stone-200 bg-white px-2 text-stone-600 hover:border-[#DC1A3C] hover:text-[#C4173A]"
                        >
                          {copied === doc.documentId
                            ? <span className="text-[11px] font-semibold text-emerald-700">Copied</span>
                            : <Copy className="h-4 w-4" />}
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-3 sm:px-4">
                      <div
                        className={`inline-flex overflow-hidden rounded-full border border-stone-200 ${canEdit ? '' : 'opacity-70'}`}
                        role="radiogroup"
                        aria-label={`Who can open ${doc.title}`}
                        title={canEdit ? undefined : 'View only — your role cannot change this'}
                      >
                        {(['public', 'private'] as const).map((v) => (
                          <button
                            key={v}
                            type="button"
                            role="radio"
                            aria-checked={vis === v}
                            disabled={!canEdit || savingVis === doc.documentId}
                            onClick={() => void setVisibility(doc, v)}
                            className={`min-h-[44px] px-3 text-[12px] font-semibold capitalize ${
                              vis === v ? 'text-white' : 'text-stone-500 hover:text-stone-900'
                            } disabled:cursor-not-allowed`}
                            style={vis === v ? { background: INK } : undefined}
                          >
                            {v}
                          </button>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-3 sm:px-4">
                      <div className="flex justify-end gap-1.5">
                        <button
                          type="button"
                          className={btn}
                          title={phase === 'live' ? 'Print / PDF' : 'Nothing published yet — opens the public page'}
                          aria-label={`Print or PDF ${doc.title}`}
                          disabled={printing === doc.documentId}
                          onClick={() => void print(doc)}
                        >
                          {printing === doc.documentId
                            ? <RubyGemMark size={16} drawing color="currentColor" />
                            : <Printer className="h-4 w-4" />}
                        </button>
                        <button
                          type="button"
                          className="inline-flex min-h-[44px] items-center justify-center rounded-lg px-3 text-[13px] font-semibold text-white"
                          style={{ background: INK }}
                          onClick={() => setSharing({ doc, visibility: vis })}
                        >
                          Share
                        </button>
                        <button
                          type="button"
                          className={`${btn} whitespace-nowrap`}
                          onClick={() => navigate(`${detailBase}/${encodeURIComponent(doc.documentId)}`)}
                        >
                          {!canEdit ? 'Open ›' : phase === 'empty' ? 'Write ›' : 'Edit ›'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {shown.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-[13px] text-stone-500">
                    No {kind === 'agreement' ? 'agreements' : 'policies'} in this product line.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <p className="px-4 pb-3.5 pt-2.5 text-[12.5px] text-stone-500 sm:px-5">
        No Retire or Delete on these — only new versions. They also appear in your library below,
        in their System documents folder.
      </p>

      {sharing && (
        <ShareDocumentModal
          documentId={sharing.doc.documentId}
          title={sharing.doc.title}
          slug={sharing.doc.slug}
          visibility={sharing.visibility}
          canEdit={canEdit}
          onClose={() => setSharing(null)}
        />
      )}
    </section>
  );
}
